import { useUsersList } from '../../../api/healthCoach/useUsersList';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';
import useCareTeamMember from './useCareTeamMember';
import { useDashboardCoach } from './useDashboardCoach';
import {
  EnrollmentStatus,
  OnboardingStatus,
  InitialVisitStatus,
  INITIAL_SORT_ORDER_BY_COLUMN,
  SORT_BY_OPTIONS,
  SORT_ORDER_OPTIONS,
  SortBy,
  SortOrder,
} from '../constants';

const SEARCH_FILTER_PARAM_KEY = 'search';
const COACH_PARAM_KEY = 'coach';
const CARE_MODEL_PARAM_KEY = 'careModel';
const USER_TYPES_PARAM_KEY = 'types';
const MEETINGS_PARAM_KEY = 'meetings';
const MEETING_NUM_DAYS_PARAM_KEY = 'meetingNumDays';
const PROVIDER_PARAM_KEY = 'provider';
const MEETING_CONFIRMED_PARAM_KEY = 'confirmed';
const PAGE_PARAM_KEY = 'page';
const ENROLLMENT_STATUS_PARAM_KEY = 'enrollment_status';
const ONBOARDING_STATUS_PARAM_KEY = 'app_onboarding_status';
const INITIAL_VISIT_STATUS_PARAM_KEY = 'initial_visit';
const RISK_STRATIFICATION_PARAM_KEY = 'risk_stratification';
const SORT_BY_PARAM_KEY = 'sort_by';
const SORT_ORDER_PARAM_KEY = 'sort_order';

// Mirror the backend's defaults so the header shows the order the API actually returns.
const DEFAULT_SORT_BY: SortBy = 'next_meeting';
const DEFAULT_SORT_ORDER: SortOrder = 'asc';

// Search only queries the API once there's enough signal to filter on.
const MIN_SEARCH_LENGTH = 3;

// Users fetched/shown per page.
const LIMIT = 100;

export const useHealthCoachDashboard = () => {
  // params in the URL
  const [searchParams, setSearchParams] = useSearchParams();

  // get authenticated user's healthie_id - enables setting them as selectedProvider by default
  const { initialSelectedProviderId, isCareTeamDataLoaded } = useCareTeamMember();
  // get the coach_id of the selected coach - enables setting them as selectedCoach by default
  const { initialSelectedCoachId, isCoachDataLoaded } = useDashboardCoach();

  // all the params coming from the URL are read and prepared to be used
  const searchFilter = searchParams.get(SEARCH_FILTER_PARAM_KEY) || '';
  const selectedCoach = searchParams.get(COACH_PARAM_KEY);
  const selectedCareModel = searchParams.get(CARE_MODEL_PARAM_KEY);
  const selectedMeetings = searchParams.get(MEETINGS_PARAM_KEY);
  const selectedMeetingNumDaysParam = searchParams.get(
    MEETING_NUM_DAYS_PARAM_KEY
  );
  const selectedMeetingNumDays = selectedMeetingNumDaysParam
    ? Number(selectedMeetingNumDaysParam)
    : undefined;
  const selectedProvider = searchParams.get(PROVIDER_PARAM_KEY);
  const selectedUserType = searchParams.get(USER_TYPES_PARAM_KEY);
  const selectedConfirmedParam = searchParams.get(MEETING_CONFIRMED_PARAM_KEY);
  const selectedMeetingConfirmed =
    selectedConfirmedParam === null ? null : selectedConfirmedParam === 'true';
  const selectedEnrollmentStatus = searchParams.get(
    ENROLLMENT_STATUS_PARAM_KEY
  );
  const selectedOnboardingStatus = searchParams.get(
    ONBOARDING_STATUS_PARAM_KEY
  );
  const selectedInitialVisitStatus = searchParams.get(
    INITIAL_VISIT_STATUS_PARAM_KEY
  );
  const selectedRiskStratification = searchParams.get(
    RISK_STRATIFICATION_PARAM_KEY
  );
  const page = Number(searchParams.get(PAGE_PARAM_KEY)) || 1;
  // Only pass through known sort values so a hand-edited URL can't send junk to the API.
  // Missing/unknown values fall back to the BE defaults.
  const sortByParam = searchParams.get(SORT_BY_PARAM_KEY);
  const sortBy: SortBy =
    SORT_BY_OPTIONS.find((option) => option === sortByParam) ||
    DEFAULT_SORT_BY;
  const sortOrderParam = searchParams.get(SORT_ORDER_PARAM_KEY);
  const sortOrder: SortOrder =
    SORT_ORDER_OPTIONS.find((option) => option === sortOrderParam) ||
    DEFAULT_SORT_ORDER;

  const offset = LIMIT * (page - 1);

  // fetch the users
  const {
    usersList: users,
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
    pageLength,
    totalResults,
  } = useUsersList({
    care_team_member_id: selectedProvider || undefined,
    coach_id: selectedCoach || undefined,
    care_model: selectedCareModel || undefined,
    user_type: selectedUserType || undefined,
    search: searchFilter || undefined,
    meetings: selectedMeetings || undefined,
    meeting_num_days: selectedMeetingNumDays,
    meeting_confirmed: selectedMeetingConfirmed || undefined,
    enrollment_status:
      (selectedEnrollmentStatus as EnrollmentStatus) || undefined,
    app_onboarding_status:
      (selectedOnboardingStatus as OnboardingStatus) || undefined,
    initial_visit:
      (selectedInitialVisitStatus as InitialVisitStatus) || undefined,
    risk_stratification: selectedRiskStratification || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
    limit: LIMIT,
    offset,
  });

  const hasNextPage = offset + (pageLength || 0) < (totalResults || 0);
  const hasPreviousPage = page > 1;
  const totalPages = Math.max(1, Math.ceil((totalResults || 0) / LIMIT));

  // Applies against the latest URL state (via the functional updater) rather
  // than a snapshot captured at render time. Note: react-router's
  // setSearchParams does NOT chain functional updates against each other —
  // each call computes `prev` from this render's params, independent of any
  // other setSearchParams call made in the same tick. So every update that
  // touches more than one key MUST go through a single updateSearchParams
  // call (object of key/value pairs), never multiple sequential calls —
  // otherwise the later call's navigate() silently overwrites the earlier
  // one's change.
  const updateSearchParams = useCallback(
    (updates: Record<string, string | boolean | undefined>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value) {
            next.set(key, String(value));
          } else {
            next.delete(key);
          }
        });
        return next;
      });
    },
    [setSearchParams]
  );

  // Tracks whether we've already seeded the default filters so we only do it
  // once per page load (not on every re-render after data arrives).
  const hasSeededDefaults = useRef(false);

  useEffect(() => {
    // Wait until both async queries have resolved before seeding defaults.
    // Without this guard, whichever query resolves first would seed its value
    // and flip hasSeededDefaults, permanently blocking the slower query's value.
    if (!isCoachDataLoaded || !isCareTeamDataLoaded) return;
    if (hasSeededDefaults.current) return;

    hasSeededDefaults.current = true;
    updateSearchParams({
      [PROVIDER_PARAM_KEY]: selectedProvider || initialSelectedProviderId,
      [COACH_PARAM_KEY]: selectedCoach || initialSelectedCoachId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoachDataLoaded, isCareTeamDataLoaded]);

  const onFilterChange = useCallback(
    (searchFilter) => {
      // Ignore 1-2 character queries; only search once cleared or at min length.
      if (searchFilter.length > 0 && searchFilter.length < MIN_SEARCH_LENGTH) {
        return;
      }
      // Search is a server-side filter, so the result set changes — reset paging.
      updateSearchParams({
        [SEARCH_FILTER_PARAM_KEY]: searchFilter,
        [PAGE_PARAM_KEY]: '',
      });
    },
    [updateSearchParams]
  );

  const onCoachChange = useCallback(
    (selectedCoachOpt) => {
      // Coach is a server-side filter, so the result set changes — reset paging.
      updateSearchParams({
        [COACH_PARAM_KEY]: selectedCoachOpt?.value || '',
        [PAGE_PARAM_KEY]: '',
      });
    },
    [updateSearchParams]
  );

  const onCareModelChange = useCallback(
    (selectedCareModelOpt) => {
      // Care model is a server-side filter, so the result set changes — reset paging.
      updateSearchParams({
        [CARE_MODEL_PARAM_KEY]: selectedCareModelOpt?.value || '',
        [PAGE_PARAM_KEY]: '',
      });
    },
    [updateSearchParams]
  );

  const onRiskStratificationChange = useCallback(
    (selectedRiskStratificationOpt) => {
      // Risk stratification is a server-side filter, so the result set changes — reset paging.
      updateSearchParams({
        [RISK_STRATIFICATION_PARAM_KEY]: selectedRiskStratificationOpt?.value || '',
        [PAGE_PARAM_KEY]: '',
      });
    },
    [updateSearchParams]
  );

  // TODO(follow-up): user_type is single-select here because the backend
  // /private/coach/users endpoint only accepts one user_type value. Switch
  // this back to multi-select once the backend supports multiple values.
  const onUserTypeChange = useCallback(
    (selectedUserTypeOpt) => {
      // User type is a server-side filter, so the result set changes — reset paging.
      updateSearchParams({
        [USER_TYPES_PARAM_KEY]: selectedUserTypeOpt?.value || '',
        [PAGE_PARAM_KEY]: '',
      });
    },
    [updateSearchParams]
  );

  const onMeetingsFilterChange = useCallback(
    (meetings?: string, meetingNumDays?: number) => {
      // Meetings is a server-side filter, so the result set changes — reset paging.
      updateSearchParams({
        [MEETINGS_PARAM_KEY]: meetings || '',
        [MEETING_NUM_DAYS_PARAM_KEY]: meetingNumDays ? String(meetingNumDays) : '',
        [PAGE_PARAM_KEY]: '',
      });
    },
    [updateSearchParams]
  );

  const onProviderChange = useCallback(
    (selectedProviderOpt) => {
      // Provider is a server-side filter — reset paging on change.
      updateSearchParams({
        [PROVIDER_PARAM_KEY]: selectedProviderOpt?.value || '',
        [PAGE_PARAM_KEY]: '',
      });
    },
    [updateSearchParams]
  );

  const onMeetingConfirmedChange = useCallback(
    (confirmed?: boolean) => {
      // Confirmed is a server-side filter, so the result set changes — reset paging.
      updateSearchParams({
        [MEETING_CONFIRMED_PARAM_KEY]: confirmed === undefined ? '' : String(confirmed),
        [PAGE_PARAM_KEY]: '',
      });
    },
    [updateSearchParams]
  );

  const onEnrollmentStatusChange = useCallback(
    (enrollmentStatus?: string) => {
      updateSearchParams({
        [ENROLLMENT_STATUS_PARAM_KEY]: enrollmentStatus || '',
        [PAGE_PARAM_KEY]: '',
      });
    },
    [updateSearchParams]
  );

  const onOnboardingStatusChange = useCallback(
    (onboardingStatus?: string) => {
      updateSearchParams({
        [ONBOARDING_STATUS_PARAM_KEY]: onboardingStatus || '',
        [PAGE_PARAM_KEY]: '',
      });
    },
    [updateSearchParams]
  );

  const onInitialVisitStatusChange = useCallback(
    (initialVisitStatus?: string) => {
      updateSearchParams({
        [INITIAL_VISIT_STATUS_PARAM_KEY]: initialVisitStatus || '',
        [PAGE_PARAM_KEY]: '',
      });
    },
    [updateSearchParams]
  );

  const onSortChange = useCallback(
    (nextSortBy: SortBy) => {
      // Clicking the active column flips the direction; a new column starts in
      // its own initial direction.
      let nextSortOrder: SortOrder = INITIAL_SORT_ORDER_BY_COLUMN[nextSortBy];
      if (nextSortBy === sortBy) {
        nextSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      }
      // Sort order changes which users land on each page — reset paging.
      updateSearchParams({
        [SORT_BY_PARAM_KEY]: nextSortBy,
        [SORT_ORDER_PARAM_KEY]: nextSortOrder,
        [PAGE_PARAM_KEY]: '',
      });
    },
    [sortBy, sortOrder, updateSearchParams]
  );

  const onPageChange = useCallback(
    (nextPage: number) => {
      // Drop the param for page 1 to keep the default URL clean.
      updateSearchParams({
        [PAGE_PARAM_KEY]: nextPage > 1 ? String(nextPage) : '',
      });
    },
    [updateSearchParams]
  );

  const clearAllFilters = useCallback(() => {
    updateSearchParams({
      [COACH_PARAM_KEY]: '',
      [CARE_MODEL_PARAM_KEY]: '',
      [USER_TYPES_PARAM_KEY]: '',
      [MEETINGS_PARAM_KEY]: '',
      [MEETING_NUM_DAYS_PARAM_KEY]: '',
      [PROVIDER_PARAM_KEY]: '',
      [MEETING_CONFIRMED_PARAM_KEY]: '',
      [PAGE_PARAM_KEY]: '',
      [ENROLLMENT_STATUS_PARAM_KEY]: '',
      [ONBOARDING_STATUS_PARAM_KEY]: '',
      [INITIAL_VISIT_STATUS_PARAM_KEY]: '',
      [RISK_STRATIFICATION_PARAM_KEY]: '',
    });
  }, [updateSearchParams]);

  // All filters are now server-side, so the fetched page is shown as-is.
  const filteredUsers = users || [];

  return {
    selectedMeetings,
    selectedMeetingNumDays,
    filteredUsers,
    isLoadingUsers,
    isFetchingUsers,
    onCareModelChange,
    onRiskStratificationChange,
    onCoachChange,
    onFilterChange,
    onMeetingsFilterChange,
    onUserTypeChange,
    onProviderChange,
    onMeetingConfirmedChange,
    onEnrollmentStatusChange,
    onOnboardingStatusChange,
    onInitialVisitStatusChange,
    onPageChange,
    onSortChange,
    clearAllFilters,
    page,
    hasNextPage,
    hasPreviousPage,
    totalPages,
    totalResults,
    searchFilter,
    selectedCareModel,
    selectedRiskStratification,
    selectedCoach,
    selectedUserType,
    selectedProvider,
    selectedMeetingConfirmed,
    selectedEnrollmentStatus,
    selectedOnboardingStatus,
    selectedInitialVisitStatus,
    sortBy,
    sortOrder,
  };
};
